<template>
  <div class="container-custom moo-sheet-page moo-membership-page" :style="styleObject">
    <div class="content-custom">
      <div class="moo-sheet-shell moo-sheet-shell--with-submit-bar">
        <MooSheetHead
          icon="workspace_premium"
          title="Activate Membership"
          @close="goBack()"
        />

        <div v-if="isLoadDone && isLogined && tier">
          <div class="gm-hero">
            <div class="gm-hero__info">
              <div class="gm-hero__label">{{ tier.tier_name }}</div>
              <div class="gm-hero__amount">$ {{ formatAmount(membershipFee) }}</div>
              <div class="gm-hero__meta" v-if="tier.membership_fee_total_format">
                One-time membership fee
              </div>
            </div>
            <span class="material-icons-round gm-hero__icon">workspace_premium</span>
          </div>

          <div class="gm-perks">
            <div class="gm-perk" v-for="(perk, index) in perks" :key="index">
              <span class="material-icons-round gm-perk__icon">check_circle</span>
              <span>{{ perk }}</span>
            </div>
          </div>

          <template v-if="tier.show_tier_description && tier.description">
            <div class="gm-section-title">Description</div>
            <div class="gm-card">
              <p class="gm-card__line">{{ tier.description }}</p>
            </div>
          </template>

          <template v-if="conditions.length">
            <div class="gm-section-title">Condition</div>
            <div class="gm-card">
              <p class="gm-card__line" v-for="(line, index) in conditions" :key="index">
                {{ line.text }}
                <span class="gm-chip" v-if="line.completed">Completed</span>
              </p>
            </div>
          </template>

          <template v-if="benefits.length">
            <div class="gm-section-title">Benefit</div>
            <div class="gm-benefits">
              <div class="gm-benefit" v-for="(benefit, index) in benefits" :key="index">
                <span class="material-icons-round gm-benefit__icon">check</span>
                <div class="gm-benefit__body">
                  <div class="gm-benefit__title">{{ benefit.title }}</div>
                  <div
                    class="gm-benefit__desc"
                    v-for="(line, i) in benefit.lines"
                    :key="i"
                  >
                    {{ line }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div class="gm-locked" v-if="!isEligible && !eligibilityPending">
            <span class="material-icons-round gm-locked__icon">lock</span>
            <div>
              <div class="gm-locked__title">
                You do not meet the condition to achieve this tier yet
              </div>
              <div
                class="gm-locked__reason"
                v-for="(reason, index) in ineligibleReasons"
                :key="index"
              >
                {{ reason }}
              </div>
            </div>
          </div>

          <div class="gm-section-title">Payment method</div>
          <div class="gm-methods">
            <button
              type="button"
              class="gm-method"
              :class="{
                'gm-method--selected': pickedPay === PAY_WALLET,
                'gm-method--disabled': !isWalletSufficient,
              }"
              :disabled="!isWalletSufficient"
              @click="pickedPay = PAY_WALLET"
            >
              <span class="gm-method__radio"></span>
              <span class="material-icons-round gm-method__icon">account_balance_wallet</span>
              <span class="gm-method__body">
                <span class="gm-method__label">Use wallet top-up</span>
                <span class="gm-method__note">
                  Top-up balance $ {{ formatAmount(walletBalance) }}
                  <template v-if="!isWalletSufficient"> — not enough</template>
                </span>
              </span>
            </button>

            <button
              type="button"
              class="gm-method"
              :class="{ 'gm-method--selected': pickedPay === PAY_CARD }"
              @click="pickedPay = PAY_CARD"
            >
              <span class="gm-method__radio"></span>
              <span class="material-icons-round gm-method__icon">credit_card</span>
              <span class="gm-method__body">
                <span class="gm-method__label">Use credit card</span>
                <span class="gm-method__note">You will be redirected to pay</span>
              </span>
            </button>
          </div>

          <SubmitOrderBar
            label="Proceed"
            :disabled="!tier || !isEligible"
            :loading="loaderActive"
            @submit="askConfirm()"
          />
        </div>
      </div>
    </div>

    <ModalConfirmSheet
      :message="confirmMessage"
      icon="workspace_premium"
      confirm-text="Proceed"
      cancel-text="Cancel"
      :process-yes="processPayment"
    />


    <GuestRewardPrompt
      :show="showGuestPrompt"
      @signin="gotoSignInFromGuestPrompt()"
      @dismiss="goBack()"
    />

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import GuestRewardPrompt from "@/components/GuestRewardPrompt.vue";
import ModalConfirmSheet from "@/components/ModalConfirmSheet.vue";
import SubmitOrderBar from "@/components/SubmitOrderBar.vue";
import MemberService from "../services/member.service";
import { tierConditions, tierBenefits } from "@/config/tier";
import { STRIPE, AIRWALLEX } from "@/config/const";
import {
  localStorageName,
  gotoMemberLogin,
  checkTierAchieveConditions,
} from "@/config/utils";
import $ from "jquery";

const PAY_WALLET = "wallet";
const PAY_CARD = "card";

export default {
  name: "GetMemberShip",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    GuestRewardPrompt,
    ModalConfirmSheet,
    SubmitOrderBar,
    MooSheetHead,
  },
  data() {
    return {
      PAY_WALLET,
      PAY_CARD,
      isLogined: false,
      isLoadDone: false,
      showGuestPrompt: false,
      branchCode: "",
      tableCode: "",
      pathType: "",
      tier: null,
      memberInfo: {},
      merchantMember: {},
      // See LoyaltyTierBenefit.vue: the tier and the member load in parallel.
      merchantMemberLoaded: false,
      pickedPay: PAY_CARD,
      paymentMode: STRIPE,
      loaderActive: false,
      snackbar: null,
      message: "",
    };
  },
  created() {
    this.pathType = this.$route.params.type;
    this.branchCode = this.$store.state.branchCode;
    this.tableCode = this.$route.params.table_code;

    const memberInfoData = JSON.parse(
      localStorage.getItem(localStorageName("memberInfo")) || "null"
    );
    this.isLogined = !!memberInfoData;

    if (this.isLogined) {
      this.getTier();
      this.getDefaultPaymentMode();
      this.getMerchantMember();
    } else {
      this.isLoadDone = true;
      this.showGuestPrompt = true;
    }
  },
  computed: {
    styleObject() {
      return {
        "--color": this.$store.state.mainThemeColorTheme2
          ? this.$store.state.mainThemeColorTheme2
          : this.$store.state.color,
      };
    },
    // Same client-side evaluation LoyaltyTierBenefit.vue's CTA uses, so the
    // screen it links to cannot disagree with it.
    eligibilityPending() {
      return !this.merchantMemberLoaded;
    },
    eligibility() {
      return checkTierAchieveConditions(this.tier, this.merchantMember);
    },
    isEligible() {
      return !this.eligibilityPending && this.eligibility.eligible;
    },
    ineligibleReasons() {
      return this.eligibility.reasons;
    },
    membershipFee() {
      return this.tier ? Number(this.tier.membership_fee_total) || 0 : 0;
    },
    // getTotalTopupAmountCanSpent, not amount_in_account — the wallet payment
    // can only draw on the top-up balance.
    walletBalance() {
      return Number(this.memberInfo.getTotalTopupAmountCanSpent) || 0;
    },
    isWalletSufficient() {
      return this.walletBalance >= this.membershipFee;
    },
    perks() {
      return [
        "Pay $ " + this.formatAmount(this.membershipFee) + " and enjoy greater perks",
        "Earn & Spend cashback in any transaction",
        "Unlimited access to vouchers & rewards",
      ];
    },
    conditions() {
      return tierConditions(this.tier);
    },
    benefits() {
      return tierBenefits(this.tier);
    },
    confirmMessage() {
      if (!this.tier) {
        return "";
      }
      const rows = [
        {
          icon: "workspace_premium",
          label: "Membership",
          value: this.tier.tier_name,
        },
        {
          icon: this.pickedPay === PAY_WALLET ? "account_balance_wallet" : "credit_card",
          label: "Pay with",
          value: this.pickedPay === PAY_WALLET ? "Wallet top-up" : "Credit card",
        },
      ];
      if (this.pickedPay === PAY_WALLET) {
        rows.push({
          icon: "savings",
          label: "Top-up balance after",
          value: "$ " + this.formatAmount(this.walletBalance - this.membershipFee),
        });
      }
      const rowsHtml = rows
        .map(
          (r) => `
        <div class="ps-row">
          <span class="material-icons-outlined ps-row-icon">${r.icon}</span>
          <span class="ps-row-label">${r.label}</span>
          <span class="ps-row-value">${r.value}</span>
        </div>`
        )
        .join("");

      return `
        <span class="ps-title">Confirm Membership</span>
        <span class="ps-amount">$ ${this.formatAmount(this.membershipFee)}</span>
        <span class="ps-rows">${rowsHtml}</span>
      `;
    },
  },
  methods: {
    formatAmount(amount) {
      return new Intl.NumberFormat("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(amount || 0);
    },
    gotoTierBenefit(query) {
      this.$router.push({
        name: "LoyaltyTierBenefit",
        query: query,
        params: {
          merchant_code: this.$store.state.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    goBack() {
      this.$router.push({
        name: "Profile",
        params: {
          merchant_code: this.$store.state.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    gotoSignInFromGuestPrompt() {
      this.showGuestPrompt = false;
      gotoMemberLogin("profile", false, "login");
    },
    askConfirm() {
      if (!this.tier || !this.isEligible) {
        return;
      }
      $(".btn-confirm-sheet-modal").click();
    },
    getTier() {
      const tierId = this.$route.params.tier_id;

      // Handed over by LoyaltyTierBenefit; only a direct URL hit has to fetch.
      const handedOver = window.history.state?.tier;
      if (handedOver && handedOver.id == tierId) {
        this.applyTier(handedOver);
        this.isLoadDone = true;
        return;
      }

      this.loaderActive = true;
      MemberService.getListTier().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.applyTier(
              (response.data.data ?? []).find((item) => item.id == tierId)
            );
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.loaderActive = false;
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Cannot load membership";
          this.setTimeOutNotification();
          this.isLoadDone = true;
          this.loaderActive = false;
        }
      );
    },
    // merchant_member (not get-profile): share_info_profile and
    // already_achieved_a_paid_tier_previously live on minty_mer_members.
    // Only paid tiers can be activated here, same guard the member site applies
    // before rendering anything.
    applyTier(tier) {
      if (tier && tier.is_paid_member_ship == 1) {
        this.tier = tier;
        this.getMemberInfo();
      } else {
        this.goBack();
      }
    },
    getMerchantMember() {
      MemberService.getMemberInfo(this.$store.state.merchantCode).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.merchantMember = response.data.data.merchant_member ?? {};
          }
          this.merchantMemberLoaded = true;
        },
        () => {
          this.merchantMemberLoaded = true;
        }
      );
    },
    getMemberInfo() {
      MemberService.getProfile().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.memberInfo = response.data.data;
            // Already on this tier — nothing to buy.
            if (this.tier && this.memberInfo.member_tier === this.tier.id) {
              this.goBack();
              return;
            }
            this.pickedPay = this.isWalletSufficient ? PAY_WALLET : PAY_CARD;
          }
        },
        () => {}
      );
    },
    getDefaultPaymentMode() {
      MemberService.getDefaultPaymentMode().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.paymentMode =
              parseInt(response.data.data.default_payment_mode) || STRIPE;
          }
        },
        () => {}
      );
    },
    processPayment() {
      if (this.loaderActive || !this.tier) {
        return;
      }
      if (this.pickedPay === PAY_WALLET) {
        this.payWithWallet();
      } else {
        this.payWithCard();
      }
    },
    payWithWallet() {
      this.loaderActive = true;
      MemberService.activatePaidMembershipWithWallet({
        amount: this.membershipFee,
        tier_id: this.tier.id,
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.gotoTierBenefit({
              membership: "success",
              tier_id: this.tier.id,
            });
            return;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.loaderActive = false;
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Payment failed";
          this.setTimeOutNotification();
          this.loaderActive = false;
        }
      );
    },
    payWithCard() {
      this.loaderActive = true;
      const merchantCode = this.$store.state.merchantCode;
      // Come back to the tier screen, which reports the outcome.
      let url = location.protocol + "//" + location.host + "/" + merchantCode;
      if (this.tableCode) {
        url +=
          "/QR/" + this.branchCode + "/" + this.tableCode + "/loyalty-tier-benefit";
      } else {
        url += "/" + this.branchCode + "/loyalty-tier-benefit";
      }

      const payment_amount = this.formatAmount(this.membershipFee);
      const dataSend = {
        images: "",
        name: "PAID MEMBERSHIP " + payment_amount,
        unit_amount: this.membershipFee,
        quantity: 1,
        member_id: this.memberInfo.id,
        isPaidMembership: 1,
        tier_id: this.tier.id,
        cancel_url: url,
      };

      // The tier is granted server-side by the Stripe/Airwallex webhook off
      // metadata.isPaidMembership — the return URL only has to land somewhere
      // sensible, it confirms nothing itself.
      const isAirwallex = this.paymentMode == AIRWALLEX;
      const request = isAirwallex
        ? MemberService.checkoutAirwallex({
            ...dataSend,
            // The BE appends airwallex_intent_id to this URL itself.
            success_url:
              url + "?membership=success&tier_id=" + this.tier.id,
          })
        : MemberService.checkoutStripe({
            ...dataSend,
            success_url:
              url +
              "?session_id={CHECKOUT_SESSION_ID}&membership=success&tier_id=" +
              this.tier.id,
          });

      request.then(
        (response) => {
          if (response.data.result.isSuccess) {
            window.location.href = response.data.data.url;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            this.loaderActive = false;
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Checkout failed";
          this.setTimeOutNotification();
          this.loaderActive = false;
        }
      );
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* Base MOO page shell — same contract TopUp.vue / MyTransactions.vue rely on. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

/* Same color-mix token recipe as TopUp.vue's --tp-*. */
.moo-membership-page {
  /* Aliases onto the shared set in assets/css/moo-sheet.css so the
     colour recipe lives in exactly one place. */
  --gm: var(--sheet);
  --gm-08: var(--sheet-08);
  --gm-12: var(--sheet-12);
  --gm-border: var(--sheet-border);
  --gm-inset: var(--sheet-inset);
}



.gm-hero {
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(0, 0, 0, 0.12) 100%
    ),
    var(--gm);
  border-radius: 20px;
  padding: 20px;
  color: #fff;
  margin-bottom: 16px;
  box-shadow: 0 10px 24px rgba(25, 28, 30, 0.14);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.gm-hero__info {
  min-width: 0;
}
.gm-hero__label {
  font-size: 12px;
  opacity: 0.85;
  margin-bottom: 4px;
}
.gm-hero__amount {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.gm-hero__meta {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 6px;
}
.gm-hero__icon {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 26px;
}

.gm-perks {
  background: #ffffff;
  border: 1px solid var(--gm-border);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.gm-perk {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #241a12;
  line-height: 1.5;
}
.gm-perk__icon {
  font-size: 18px;
  color: var(--gm);
  flex-shrink: 0;
}

.gm-locked {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: var(--gm-08);
  border: 1px dashed var(--gm-border);
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 20px;
}
.gm-locked__icon {
  font-size: 18px;
  color: var(--gm);
  flex-shrink: 0;
}
.gm-locked__title {
  font-size: 13px;
  font-weight: 700;
  color: #241a12;
}
.gm-locked__reason {
  font-size: 12px;
  color: #857372;
  margin-top: 3px;
}

.gm-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  margin-bottom: 10px;
}

.gm-card {
  background: #ffffff;
  border: 1px solid var(--gm-border);
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 20px;
}
.gm-card__line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #6b5d55;
  margin: 0 0 6px;
}
.gm-card__line:last-child {
  margin-bottom: 0;
}
.gm-chip {
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
  color: var(--gm);
  background: var(--gm-12);
}

.gm-benefits {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}
.gm-benefit {
  display: flex;
  gap: 10px;
}
.gm-benefit__icon {
  font-size: 16px;
  color: var(--gm);
  flex-shrink: 0;
  margin-top: 2px;
}
.gm-benefit__body {
  min-width: 0;
}
.gm-benefit__title {
  font-size: 13px;
  font-weight: 600;
  color: #241a12;
  line-height: 1.5;
}
.gm-benefit__desc {
  font-size: 12px;
  color: #857372;
  line-height: 1.5;
  margin-top: 3px;
}

.gm-methods {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.gm-method {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: 1.5px solid var(--gm-border);
  border-radius: 16px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.gm-method--selected {
  border-color: var(--gm);
  background: var(--gm-08);
}
.gm-method--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.gm-method__radio {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--gm-border);
  background: #fff;
  flex-shrink: 0;
}
.gm-method--selected .gm-method__radio::after {
  content: "";
  display: block;
  width: 8px;
  height: 8px;
  margin: 2.5px auto;
  border-radius: 50%;
  background: var(--gm);
}
.gm-method__icon {
  font-size: 20px;
  color: var(--gm);
  flex-shrink: 0;
}
.gm-method__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.gm-method__label {
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
}
.gm-method__note {
  font-size: 12px;
  color: #857372;
  margin-top: 2px;
}

</style>
